/**
 * cro-core.js — behavior for CRO elements (namespaced cro-*).
 * One small deferred file; every feature no-ops when its element is absent.
 *
 *  2. Card quick-add (C5/H7) — single-variant one-tap add via /cart/add.js,
 *     opens the existing cart drawer through window.CartDrawerHelpers.
 *  3. Sticky ATC bar (P4) — appears whenever the real ATC / Buy Now buttons are
 *     off-screen (either direction), at every viewport width;
 *     its buttons proxy the real ATC / Buy Now buttons so all validation and
 *     inventory logic stays in one place (component-product-form.js).
 *  4. Newsletter popup (G8) — timed, frequency-capped via localStorage.
 *  5. Collection "Load more" (C8) — fetches the next crawlable page and appends
 *     its product cards; falls back to normal navigation without JS.
 *  6. Back-to-top (C8) — appears after ~2 screens of scroll.
 * 10. PDP offer code copy (P2 IN) — one-tap copy of a coupon code from the offers card;
 *     the button is revealed by JS, so it never appears unless it can actually copy.
 */
(function () {
  'use strict';

  var POPUP_KEY = 'cro:popup:last-dismissed';

  document.addEventListener('DOMContentLoaded', function () {
    initQuickAdd();
    initStickyAtc();
    initPopup();
    initLoadMore();
    initBackToTop();
    initVariantChangeRelay();
    initPdpLowStock();
    initDispatchCountdown();
    initFbt();
    initPricePresets();
    initOfferCopy();
  });

  /* ---- PDP variant-change relay ----
     component-product-form.js updates the hidden [data-variant-id] input a tick
     after an option button is clicked. Broadcast one custom event so every CRO
     element (low-stock line, sticky save chip, FBT anchor) stays in sync. */
  function initVariantChangeRelay() {
    var input = document.querySelector('.pdp-cart-form [data-variant-id]');
    if (!input) return;
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.variant-btn, .swatch-btn')) return;
      window.setTimeout(function () {
        document.dispatchEvent(new CustomEvent('cro:variant-changed', {
          detail: { variantId: String(input.value) }
        }));
      }, 60);
    });
  }

  /* Parsed once on demand from the buybox's embedded product JSON. */
  var _croProductData = null;
  function getProductData() {
    if (_croProductData) return _croProductData;
    var el = document.querySelector('script[id^="product-data-"]');
    if (!el) return null;
    try { _croProductData = JSON.parse(el.textContent); } catch (e) { return null; }
    return _croProductData;
  }

  /* ---- 2. Card quick-add ---- */
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cro-quick-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      var originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = btn.getAttribute('data-str-adding') || originalText;

      var addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1, sections: 'cart-drawer' })
      })
        .then(function (res) {
          if (!res.ok) {
            // Read the body: a card rendered while the product was in stock can be
            // clicked after it sells out, and Shopify explains that in a 422
            // `description`. The old code threw a bare Error, so the button just
            // said "Adding…", reverted, and nothing happened — no cart, no reason.
            return res.json()
              .catch(function () { return {}; })
              .then(function (d) {
                var err = new Error(d.description || btn.getAttribute('data-str-error') || 'Could not add to cart.');
                err.shopperFacing = true;
                throw err;
              });
          }
          return res.json();
        })
        .then(function (data) {
          btn.classList.add('is-added');
          btn.textContent = btn.getAttribute('data-str-added') || originalText;
          if (window.CartDrawerHelpers && window.CartDrawerHelpers.openCartDrawerWithContent) {
            window.CartDrawerHelpers.openCartDrawerWithContent({
              triggerElement: btn,
              sections: data.sections
            });
          }
          window.setTimeout(function () {
            btn.classList.remove('is-added');
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2000);
        })
        .catch(function (err) {
          // Surface it on the card itself — role="status" so it is announced.
          var card = btn.closest('.product-card');
          var msg = card ? card.querySelector('[data-cro-quick-add-error]') : null;
          if (msg) {
            msg.textContent = (err && err.shopperFacing)
              ? err.message
              : (btn.getAttribute('data-str-error') || 'Could not add to cart.');
            msg.hidden = false;
            window.setTimeout(function () { msg.hidden = true; }, 5000);
          }
          btn.textContent = originalText;
          btn.disabled = false;
        });
    });
  }

  /* ---- 3. Sticky ATC ---- */
  function initStickyAtc() {
    var bar = document.querySelector('[data-cro-sticky-atc]');
    var buybox = document.querySelector('.pdp-buybox');
    if (!bar || !buybox) return;

    document.body.classList.add('cro-has-sticky-atc');

    // rAF-throttled scroll check (not IntersectionObserver — IO proved unreliable
    // under viewport emulation, and a couple of rect reads per frame are cheap).
    //
    // The bar tracks the REAL buttons, not the buybox: on desktop the buy column is
    // position:sticky, so the buybox can still intersect the viewport long after the
    // buttons themselves have scrolled out of it. And "out of view" means out in
    // either direction — scrolled past (bottom above the viewport) or not yet reached
    // (top below it) — so scrolling back up hides the bar again the moment the real
    // buttons return, and shows it again if you scroll up past them.
    var anchorCache = [];
    function anchors() {
      var stale = !anchorCache.length;
      for (var i = 0; i < anchorCache.length && !stale; i++) {
        if (!anchorCache[i].isConnected) stale = true;
      }
      if (stale) {
        anchorCache = [
          document.querySelector('[data-add-to-cart]'),
          document.querySelector('[data-buy-now]')
        ].filter(Boolean);
      }
      return anchorCache.length ? anchorCache : [buybox];
    }

    function realButtonsOnScreen() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var els = anchors();
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        if (r.bottom > 0 && r.top < vh && r.width > 0 && r.height > 0) return true;
      }
      return false;
    }

    var ticking = false;
    function update() {
      bar.classList.toggle('is-visible', !realButtonsOnScreen());
    }
    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    // A resize (or a mobile URL-bar collapse) moves the buttons relative to the
    // viewport without a scroll event; without this the bar can be left stuck on.
    window.addEventListener('resize', schedule, { passive: true });
    update();

    bar.addEventListener('click', function (e) {
      var proxy = e.target.closest('[data-cro-proxy]');
      if (!proxy) return;
      var target = document.querySelector(proxy.getAttribute('data-cro-proxy'));
      if (!target) return;

      // The real button's validation errors render into [data-form-error] INSIDE
      // the buybox — which, by definition, is scrolled off-screen whenever this
      // bar is visible. So tapping sticky "Add to cart" on a product whose size
      // wasn't chosen looked like a completely dead button. Bring the buybox back
      // into view so the shopper can see what is being asked of them.
      if (target.disabled || !stickySelectionComplete()) {
        var buybox = document.querySelector('.main-product .variant-option') ||
          document.querySelector('.pdp-buybox');
        if (buybox && typeof buybox.scrollIntoView === 'function') {
          buybox.scrollIntoView({ block: 'center' });
        }
        // Let the real button run its own validation so it renders its own error.
        if (!target.disabled) target.click();
        return;
      }

      target.click();
    });

    /* Mirrors AddToCartForm._getValidatedOrder()'s explicit-choice rule. */
    function stickySelectionComplete() {
      var section = document.querySelector('.main-product');
      if (!section) return true;
      var groups = section.querySelectorAll('.variant-option');
      for (var i = 0; i < groups.length; i++) {
        if (groups[i].querySelectorAll('[data-value]').length <= 1) continue;
        if (!groups[i].hasAttribute('data-user-selected')) return false;
      }
      return true;
    }

    // Keep the sticky price in sync when the variant changes.
    var priceEl = bar.querySelector('[data-cro-sticky-price]');
    var srcPrice = document.querySelector('.pdp-price [data-price]');
    if (priceEl && srcPrice && 'MutationObserver' in window) {
      new MutationObserver(function () {
        priceEl.textContent = srcPrice.textContent;
      }).observe(srcPrice, { childList: true, characterData: true, subtree: true });
    }

    // Keep the "% OFF" chip honest across variant changes (G6/T8).
    var saveEl = bar.querySelector('[data-cro-sticky-save]');
    if (saveEl) {
      document.addEventListener('cro:variant-changed', function (e) {
        var data = getProductData();
        if (!data || !data.variants) return;
        var v = data.variants.filter(function (x) { return String(x.id) === e.detail.variantId; })[0];
        if (v && v.compare_at_price && v.compare_at_price > v.price) {
          var pct = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
          saveEl.textContent = pct + '% ' + (saveEl.getAttribute('data-str-off') || '');
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      });
    }

    // Offscreen state must not be reachable: sync aria-hidden with visibility
    // (CSS also toggles `visibility` so the buttons drop out of the tab order).
    var ariaSync = new MutationObserver(function () {
      bar.setAttribute('aria-hidden', String(!bar.classList.contains('is-visible')));
    });
    ariaSync.observe(bar, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---- 4. Newsletter popup ---- */
  function initPopup() {
    var popup = document.querySelector('[data-cro-popup]');
    if (!popup) return;

    var success = popup.querySelector('[data-cro-popup-success]');
    var frequencyDays = parseInt(popup.getAttribute('data-frequency-days') || '14', 10);
    var delay = parseInt(popup.getAttribute('data-delay') || '5000', 10);

    function markSeen() {
      try { localStorage.setItem(POPUP_KEY, String(Date.now())); } catch (e) { /* noop */ }
    }

    function close() {
      popup.classList.remove('is-open');
      markSeen();
      window.setTimeout(function () { popup.hidden = true; }, 400);
    }

    popup.querySelectorAll('[data-cro-popup-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    function open() {
      popup.hidden = false;
      // The frequency cap is stamped when the popup is SHOWN, not only when it is
      // dismissed. Previously the key was written solely inside close(), so a
      // shopper who simply ignored the sheet and navigated on got it again 5s
      // later on the next page — and the next, forever. Being shown is what the
      // "once every N days" cap is about.
      markSeen();
      // Double-rAF so the transform transition runs after unhide.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { popup.classList.add('is-open'); });
      });
    }

    // A successful signup posts back with the form anchor — show the confirmation.
    if (success) {
      open();
      return;
    }

    try {
      var last = parseInt(localStorage.getItem(POPUP_KEY) || '0', 10);
      if (last && Date.now() - last < frequencyDays * 86400000) return;
    } catch (e) { /* storage unavailable — fall through, still frequency-capped per session */ }

    window.setTimeout(open, delay);
  }

  /* ---- 5. Collection load more ----
     Delegated from `document`, NOT from the [data-cro-load-more] wrapper. The
     wrapper lives inside #CollectionContent, which the AJAX filter replaces
     wholesale — binding to it meant load-more went dead after any filter/sort. */
  var _loadMoreBusy = false;

  function initLoadMore() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cro-load-more-btn]');
      if (!btn) return;
      e.preventDefault();

      // The trigger is an <a> (crawlable, degrades without JS). `disabled` is a
      // no-op expando on an anchor, so it cannot guard re-entry — a double-click
      // fired two fetches and appended the same page twice. Guard on a flag.
      if (_loadMoreBusy) return;

      var nextUrl = btn.getAttribute('data-next-url');
      if (!nextUrl) return;

      var wrap = btn.closest('[data-cro-load-more]');
      _loadMoreBusy = true;
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');

      fetch(nextUrl)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var grid = document.getElementById('ProductGrid');
          if (!grid) return;

          var newCards = doc.querySelectorAll('#ProductGrid > .product-card');
          var firstNew = null;
          newCards.forEach(function (card) {
            var added = document.importNode(card, true);
            if (!firstNew) firstNew = added;
            grid.appendChild(added);
          });

          // Keep the address bar in step, so Back returns to the same depth
          // instead of snapping to page 1 with the extra products gone.
          try {
            window.history.replaceState({}, '', nextUrl);
          } catch (err) { /* history unavailable — non-critical */ }

          var newBtn = doc.querySelector('[data-cro-load-more-btn]');
          if (newBtn && newBtn.getAttribute('data-next-url')) {
            btn.setAttribute('data-next-url', newBtn.getAttribute('data-next-url'));
            btn.setAttribute('href', newBtn.getAttribute('data-next-url'));
            btn.removeAttribute('aria-busy');
            btn.removeAttribute('aria-disabled');
            btn.classList.remove('is-loading');
          } else {
            // Last page: the button that currently holds focus is about to be
            // removed. Hand focus to the first newly-loaded card first, or the
            // next Tab restarts from the top of the document.
            var hadFocus = document.activeElement === btn;
            btn.remove();
            if (hadFocus && firstNew) {
              var link = firstNew.querySelector('a');
              if (link) {
                link.setAttribute('tabindex', '-1');
                link.focus();
              }
            }
          }

          var count = wrap ? wrap.querySelector('[data-cro-load-more-count]') : null;
          if (count && count.getAttribute('data-template')) {
            var shown = grid.querySelectorAll('.product-card').length;
            count.textContent = count.getAttribute('data-template')
              .replace('[shown]', String(shown))
              .replace('[total]', count.getAttribute('data-total') || String(shown));
          }
        })
        .catch(function () {
          // Graceful fallback: navigate to the next page.
          window.location.href = nextUrl;
        })
        .finally(function () {
          _loadMoreBusy = false;
        });
    });
  }

  /* ---- 9. Price preset buttons (C2 IN) ----
     Fill the native min/max price inputs and reuse the theme's own apply flow —
     filtering itself stays 100% native Shopify. */
  function initPricePresets() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cro-price-preset');
      if (!btn) return;
      var wrap = btn.closest('[data-cro-price-presets]');
      var group = btn.closest('[data-filter-group="price"]');
      if (!wrap || !group) return;
      var inputs = group.querySelectorAll('.filter-price-inputs input');
      if (inputs.length < 2) return;
      inputs[0].value = btn.getAttribute('data-min');
      inputs[1].value = btn.getAttribute('data-max');
      inputs.forEach(function (input) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (wrap.getAttribute('data-context') === 'desktop') {
        var apply = group.querySelector('[data-price-apply]');
        if (apply) apply.click();
      }
    });
  }

  /* ---- 7. PDP low-stock line: keep honest across variant changes (P7) ----
     Reads the server-rendered per-variant inventory map and re-renders the
     urgency line when the selected variant changes (hidden input [data-variant-id]
     is updated by component-product-form.js after each option click). */
  function initPdpLowStock() {
    var line = document.querySelector('[data-cro-low-stock]');
    var dataEl = document.querySelector('[data-cro-inventory]');
    var input = document.querySelector('.pdp-cart-form [data-variant-id]');
    if (!line || !dataEl || !input) return;

    var map;
    try { map = JSON.parse(dataEl.textContent); } catch (e) { return; }

    // Merchant-set trigger, rendered into the map by pdp-block.liquid. Falls back to
    // the old hardcoded window if an older cached template omits it.
    var threshold = typeof map.threshold === 'number' ? map.threshold : 9;

    function update() {
      var v = map.variants[input.value];
      if (v && v.qty > 0 && v.qty <= threshold) {
        var tpl = (map.sizeOptionIndex > -1 && v.option)
          ? map.strings.withOption.replace('[O]', v.option)
          : map.strings.plain;
        line.textContent = tpl.replace('[N]', v.qty);
        line.hidden = false;
      } else {
        line.hidden = true;
        line.textContent = '';
      }
    }

    document.addEventListener('cro:variant-changed', update);
  }

  /* ---- 9. PDP dispatch-cutoff countdown (P7/T5) ----
     Ticks toward a FIXED wall-clock deadline that cro-urgency-dispatch.liquid computed
     server-side in the STORE's timezone. Two consequences worth keeping:

       - It never resets. Reloading, reopening, or sitting on the page for an hour all
         leave the same instant arriving. A countdown that restarts on page load is a
         fake deadline, and that is precisely what regulators name as a dark pattern.
       - When it expires, the line goes AWAY rather than rolling over to a fresh number.
         The next render computes the next real cutoff. We never promise same-day
         dispatch a second past the point the merchant can honour it. */
  function initDispatchCountdown() {
    var el = document.querySelector('[data-cro-dispatch]');
    if (!el) return;

    var textEl = el.querySelector('[data-cro-dispatch-text]');
    var deadline = parseInt(el.getAttribute('data-deadline'), 10);
    var template = el.getAttribute('data-template') || '';
    if (!textEl || !deadline || template.indexOf('[T]') === -1) return;

    var timer = null;

    function render() {
      var remaining = deadline - Math.floor(Date.now() / 1000);

      if (remaining <= 0) {
        el.hidden = true;
        if (timer) clearInterval(timer);
        return;
      }

      var h = Math.floor(remaining / 3600);
      var m = Math.floor((remaining % 3600) / 60);
      var s = remaining % 60;

      // Under an hour the seconds are the point; above it they are just noise.
      var pretty = h > 0 ? h + 'h ' + m + 'm' : m + 'm ' + s + 's';
      textEl.textContent = template.replace('[T]', pretty);
    }

    render();
    timer = setInterval(render, 1000);

    // Tabs throttle timers in the background, so the clock drifts while hidden.
    // Re-render the moment we are looked at again rather than showing a stale number.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) render();
    });
  }

  /* ---- 8. Frequently bought together (P9) ----
     Live combined total from checked rows; adds all selected variants in one
     /cart/add.js call and opens the drawer. Prices come from data attributes
     rendered server-side (real variant prices, in cents). */
  function initFbt() {
    var root = document.querySelector('[data-cro-fbt]');
    if (!root) return;

    var moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';
    var totalEl = root.querySelector('[data-cro-fbt-total]');
    var compareEl = root.querySelector('[data-cro-fbt-compare]');
    var savingsEl = root.querySelector('[data-cro-fbt-savings]');
    var countEl = root.querySelector('[data-cro-fbt-count]');
    var addBtn = root.querySelector('[data-cro-fbt-add]');
    var errorEl = root.querySelector('[data-cro-fbt-error]');

    function checkedInputs() {
      return Array.prototype.filter.call(
        root.querySelectorAll('[data-cro-fbt-item], [data-cro-fbt-anchor]'),
        function (el) { return el.checked; }
      );
    }

    // Use the shared formatter, not a hardcoded 2-decimal en-IN one. The old
    // version always emitted decimals, so on a store using {{amount_no_decimals}}
    // the row prices read "₹4,500" while the bundle total read "₹4,500.00".
    function formatMoney(cents) {
      if (window.Theme && window.Theme.formatMoney) return window.Theme.formatMoney(cents);
      return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, String((cents || 0) / 100));
    }

    /**
     * The anchor product is the PDP's own product. Adding it must obey exactly the
     * same rule the buybox enforces: every multi-value option explicitly chosen.
     * This handler POSTs data-variant-id directly and never goes through
     * AddToCartForm._getValidatedOrder(), so without this check "Add selected"
     * silently added whatever size happened to be first available.
     */
    function anchorSelectionValid() {
      var section = document.querySelector('.main-product');
      if (!section) return true;

      var groups = section.querySelectorAll('.variant-option');
      for (var i = 0; i < groups.length; i++) {
        var group = groups[i];
        // Only enforce where there is a real choice to make.
        if (group.querySelectorAll('[data-value]').length <= 1) continue;
        if (!group.hasAttribute('data-user-selected')) return false;
      }

      var input = section.querySelector('[data-variant-id]');
      return !!(input && input.value);
    }

    function showFbtError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function cents(el, attr) {
      return parseInt(el.getAttribute(attr), 10) || 0;
    }

    /**
     * The summary is the whole promise of this module, so every part of it moves
     * together: how many items are in the bundle, what they cost, and — only when the
     * compare-at prices are real — what the shopper saves. The savings figure is the
     * sum of genuine compare-at reductions, never an invented "bundle discount".
     */
    function updateTotal() {
      var checked = checkedInputs();
      var total = 0;
      var compare = 0;

      checked.forEach(function (el) {
        var price = cents(el, 'data-price');
        total += price;
        // A variant with no compare-at falls back to its own price, so it contributes
        // nothing to the savings rather than dragging the compare total below it.
        compare += Math.max(cents(el, 'data-compare'), price);
      });

      if (totalEl) totalEl.textContent = formatMoney(total);

      if (countEl) {
        var countTpl = checked.length === 1
          ? (root.getAttribute('data-str-count-one') || '1 item selected')
          : (root.getAttribute('data-str-count-other') || '%% items selected');
        countEl.textContent = countTpl.replace('%%', String(checked.length));
      }

      var saved = compare - total;
      if (compareEl) {
        compareEl.textContent = saved > 0 ? formatMoney(compare) : '';
        compareEl.hidden = saved <= 0;
      }
      if (savingsEl) {
        var savingsTpl = root.getAttribute('data-str-savings') || 'You save %%';
        savingsEl.textContent = savingsTpl.replace('%%', formatMoney(saved));
        savingsEl.hidden = saved <= 0;
      }
    }

    root.addEventListener('change', updateTotal);
    updateTotal();

    // BLOCKER fix (P9/T8): the anchor row must follow the buybox's selected
    // variant — id, price and displayed variant title — so "Add selected"
    // never silently adds the wrong size and the shown total stays true.
    var anchorInput = root.querySelector('[data-cro-fbt-anchor]');
    if (anchorInput) {
      document.addEventListener('cro:variant-changed', function (e) {
        var data = getProductData();
        if (!data || !data.variants) return;
        var v = data.variants.filter(function (x) { return String(x.id) === e.detail.variantId; })[0];
        if (!v) {
          // The buybox no longer resolves a variant (impossible combination, so
          // it cleared its hidden input). Drop the stale id and price, or the
          // bundle would keep offering — and adding — the previous variant.
          anchorInput.removeAttribute('data-variant-id');
          anchorInput.setAttribute('data-price', '0');
          anchorInput.setAttribute('data-compare', '0');
          updateTotal();
          return;
        }
        anchorInput.setAttribute('data-variant-id', String(v.id));
        anchorInput.setAttribute('data-price', String(v.price));
        // Keep the compare-at in step with the price, or the bundle's "You save" line
        // keeps quoting the previous variant's discount after a size change.
        anchorInput.setAttribute('data-compare', String(Math.max(v.compare_at_price || 0, v.price)));
        var titleEl = root.querySelector('[data-cro-fbt-anchor-variant]');
        if (titleEl && v.title && v.title !== 'Default Title') titleEl.textContent = v.title;
        var priceDisplay = root.querySelector('[data-cro-fbt-anchor-price]');
        if (priceDisplay) priceDisplay.textContent = formatMoney(v.price);
        updateTotal();
      });
    }

    if (!addBtn) return;
    addBtn.addEventListener('click', function () {
      if (errorEl) errorEl.hidden = true;

      // The anchor is this page's product — it must not be added on an implicit
      // default. Send the shopper to the buybox to choose, exactly as the main
      // Add to cart button would have made them.
      var anchor = root.querySelector('[data-cro-fbt-anchor]');
      if (anchor && anchor.checked && !anchorSelectionValid()) {
        showFbtError(addBtn.getAttribute('data-str-select-options') || 'Please choose your options above');
        var buybox = document.querySelector('.main-product .variant-option') || document.querySelector('.main-product');
        if (buybox && typeof buybox.scrollIntoView === 'function') {
          buybox.scrollIntoView({ block: 'center' });
        }
        return;
      }

      var items = checkedInputs()
        .map(function (el) {
          return { id: parseInt(el.getAttribute('data-variant-id'), 10), quantity: 1 };
        })
        .filter(function (item) { return item.id && !isNaN(item.id); });
      if (!items.length) return;

      var original = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = addBtn.getAttribute('data-str-adding') || original;
      if (errorEl) errorEl.hidden = true;

      var addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items, sections: 'cart-drawer' })
      })
        .then(function (res) {
          if (!res.ok) {
            // Read the body defensively: a proxy/5xx returns HTML, and letting
            // res.json() reject surfaced the raw parse error ("Unexpected token
            // '<'...") to the shopper as the error message.
            return res.json()
              .catch(function () { return {}; })
              .then(function (d) {
                var err = new Error(d.description || addBtn.getAttribute('data-str-error') || 'Could not add to cart. Please try again.');
                err.shopperFacing = true;
                throw err;
              });
          }
          return res.json();
        })
        .then(function (data) {
          addBtn.textContent = addBtn.getAttribute('data-str-added') || original;
          if (window.CartDrawerHelpers && window.CartDrawerHelpers.openCartDrawerWithContent) {
            window.CartDrawerHelpers.openCartDrawerWithContent({
              triggerElement: addBtn,
              sections: data.sections
            });
          }
          window.setTimeout(function () {
            addBtn.textContent = original;
            addBtn.disabled = false;
          }, 2000);
        })
        .catch(function (err) {
          // Only ever show a message we authored. A network/parse failure must
          // not print its exception text into the storefront.
          showFbtError(
            err && err.shopperFacing
              ? err.message
              : (addBtn.getAttribute('data-str-error') || 'Could not add to cart. Please try again.')
          );
          addBtn.textContent = original;
          addBtn.disabled = false;
        });
    });
  }

  /* ---- 10. PDP offer code copy (P2 IN) ----
     The offers card renders each coupon code with a Copy button that ships `hidden`.
     JS reveals it — a button that cannot copy is worse than no button, so a shopper
     without JS (or with it broken) simply reads the code off the chip instead.

     Delegated from the document rather than bound per button: the theme editor injects,
     re-renders and reorders the offers block without a page load, and a listener bound
     at DOMContentLoaded would be dead on every button that arrives after it. */
  var COPIED_MS = 2000;

  function initOfferCopy() {
    revealCopyButtons(document);

    // Section re-render in the theme editor: the new markup ships hidden buttons again.
    document.addEventListener('shopify:section:load', function (e) {
      revealCopyButtons(e.target);
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-cro-copy-code]') : null;
      if (!btn || btn.disabled) return;
      copyOfferCode(btn);
    });
  }

  function revealCopyButtons(root) {
    var btns = root.querySelectorAll ? root.querySelectorAll('[data-cro-copy-code]') : [];
    for (var i = 0; i < btns.length; i++) btns[i].hidden = false;
  }

  function copyOfferCode(btn) {
    var code = btn.getAttribute('data-cro-copy-code') || '';
    if (!code) return;

    writeToClipboard(code).then(
      function () { flashCopyResult(btn, true); },
      function () { flashCopyResult(btn, false); }
    );
  }

  /* navigator.clipboard is undefined on a non-secure origin, and Safari rejects it when
     the call is not judged user-initiated. The textarea + execCommand path is the
     fallback both cases land on — deprecated, but still the only thing that works there,
     and the alternative is a button that silently does nothing. */
  function writeToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      // Off-screen but still focusable — display:none would make select() a no-op.
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        if (document.execCommand('copy')) resolve();
        else reject(new Error('copy rejected'));
      } catch (err) {
        reject(err);
      } finally {
        document.body.removeChild(ta);
      }
    });
  }

  function flashCopyResult(btn, ok) {
    var label = btn.querySelector('[data-cro-copy-label]');
    var status = btn.parentNode.querySelector('[data-cro-copy-status]');
    var idle = btn.getAttribute('data-str-copy') || 'Copy';

    if (label) {
      label.textContent = ok
        ? (btn.getAttribute('data-str-copied') || 'Copied')
        : idle;
    }
    btn.classList.toggle('is-copied', ok);
    btn.classList.toggle('is-error', !ok);

    /* The button's accessible name is pinned by aria-label, so swapping the visible
       label announces nothing. This live region is the only announcement — and on
       failure it is also the only place the shopper is told to copy the code by hand. */
    if (status) {
      status.textContent = ok
        ? (btn.getAttribute('data-str-status') || 'Code copied')
        : (btn.getAttribute('data-str-error') || 'Could not copy the code.');
    }

    // A second click mid-flash restarts the window rather than cutting it short.
    if (btn._croCopyTimer) clearTimeout(btn._croCopyTimer);
    btn._croCopyTimer = setTimeout(function () {
      if (label) label.textContent = idle;
      btn.classList.remove('is-copied', 'is-error');
      if (status) status.textContent = '';
      btn._croCopyTimer = null;
    }, COPIED_MS);
  }

  /* ---- 6. Back to top ---- */
  function initBackToTop() {
    var btn = document.querySelector('[data-cro-back-to-top]');
    if (!btn) return;
    var threshold = window.innerHeight * 2;
    var ticking = false;

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        btn.classList.toggle('is-visible', window.scrollY > threshold);
        ticking = false;
      });
    }, { passive: true });

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
})();
